import { useState, useEffect } from 'react';

import staticdata, { resourceCatalogList } from '@lcsc-ui/apis/staticdata';
import WwwNodeApi from '@lcsc-ui/apis/www';
import Toolbar from '@lcsc-ui/components/ToolBar';
import BaseLayout from '@lcsc-ui/layouts/baseLayout';
import { baseURL } from '@lcsc-ui/utils/baseUrl';
import { buriedPoint } from '@lcsc-ui/utils/buriedPoint';
import ecpCurl, { asyncAll } from '@lcsc-ui/utils/ecpCurl';
import Utils from '@lcsc-ui/utils/Utils';
import { Breadcrumb } from 'antd';
import Head from 'next/head';
import { NextSeo } from 'next-seo';

import listApi from '@/apis/list';
import wwwApi from '@/apis/www';
import SelectionDetail from '@/features/selection/Detail';

export default function Index({
  id,
  resourceData,
  websiteSectionList,
  headerAd,
  selectionProductDetailData,
  searchResultData,
}) {
  const [isLgScreen, setIsLgScreen] = useState(null);
  const [catalogList, setCatalogList] = useState(null);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    resourceCatalogList().then((res) => {
      setCatalogList(res?.result);
    });
    buriedPoint({
      ty: 'enter',
      cp: 'selection_detail',
      op: 'view',
      pf: 'pc',
      data: {
        uuid: id,
      },
    });
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleResize = () => {
    setIsLgScreen(Utils.isLgScreen());
  };

  const { productModel, projectDesc, selectionModule, selectionSegment, imgList } = selectionProductDetailData;
  const pathName = 'selection';
  const selectionModuleText = selectionModule?.replace(/^\d+\./, '');
  const metadata = {
    title: `基于${productModel}的${selectionSegment}的${selectionModuleText}方案设计方案参考-立创商城`,
    description: `基于${productModel}的${selectionSegment}的${selectionModuleText}设计方案参考介绍，${projectDesc.length > 60 ? projectDesc.slice(0, 60) + '...' : projectDesc}`,
    additionalMetaTags: [
      {
        name: 'keywords',
        content: `${productModel},${selectionModuleText}参考设计,立创商城,立创芯应用`,
      },
    ],
  };

  let imageUrl = '';
  if (imgList?.length) {
    imageUrl = resourceData?.projectConfigVO?.lcscImageUrl + (imgList[0].cropImgUrl || imgList[0].originImgUrl);
  }

  const JsonLd = {
    '@context': 'http://schema.org',
    '@type': 'Article',
    headline: `基于${productModel}的${selectionSegment}方案`,
    keywords: [productModel, `${selectionSegment}方案`, '立创商城', '立创芯应用'],
    description: projectDesc,
    articleBody: projectDesc,
    image: imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '立创商城',
    },
  };

  const MicroData = (
    <div itemScope itemType="https://schema.org/Article">
      <meta itemProp="headline" content={`基于${productModel}的${selectionSegment}方案`} />
      <meta itemProp="keywords" content={`${productModel},${selectionSegment}方案,立创商城,立创芯应用`} />
      <meta itemProp="description" content={projectDesc} />
      <meta itemProp="articleBody" content={projectDesc} />
      <meta itemProp="image" content={imageUrl} />
      <div itemProp="publisher" itemScope itemType="https://schema.org/Organization">
        <meta itemprop="name" content="立创商城" />
      </div>
    </div>
  );

  return (
    <>
      <NextSeo {...metadata}></NextSeo>
      <Head>
        <link rel="canonical" href={resourceData?.projectConfigVO?.lcscWwwUrl + `/${pathName}/${id}.html`} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(JsonLd) }} />
      </Head>
      {MicroData}
      <BaseLayout
        resourceData={resourceData}
        websiteSectionList={websiteSectionList}
        catalogList={catalogList}
        headerAd={headerAd}
        isLgScreen={isLgScreen}
        notIndexHeaderAdapter
        onlySScreen
        isNeedTopAd
        isShowBottomAd
      >
        <div className="w-full h-full bg-[#F6F8FA]">
          <section className="w-[1200px] mx-auto pt-[16px] pb-[60px]">
            <div className="flex w-full justify-between items-center">
              <Breadcrumb data-spm="bc" separator=">">
                <Breadcrumb.Item data-spm="1" className="hover:!text-[#0093e6]" href={baseURL}>
                  首页
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  data-spm="2"
                  className="hover:!text-[#0093e6]"
                  href={resourceData?.projectConfigVO?.lcscWwwUrl + '/selection.html'}
                >
                  立创芯应用
                </Breadcrumb.Item>
                <Breadcrumb.Item className="text-[#0093e6]">应用详情页</Breadcrumb.Item>
              </Breadcrumb>
            </div>
            <SelectionDetail
              selectionProductDetailData={selectionProductDetailData}
              searchResultData={searchResultData}
            />
          </section>
        </div>
        <Toolbar></Toolbar>
      </BaseLayout>
    </>
  );
}

export async function getStaticPaths() {
  return {
    paths: [].map((item) => ({
      params: { id: item.toString() },
    })),
    fallback: 'blocking',
  };
}

export async function getStaticProps(ctx) {
  const { params } = ctx;
  const id = params.id.replace('.html', '');

  if (!id) {
    return {
      notFound: true,
    };
  }

  const [resourceData, websiteSectionList, headerAd, selectionProductDetailData, wwwStockpileHistorySwitch, notFound] =
    await asyncAll([
      ecpCurl(staticdata.resourceList, { enableCache: true }),
      ecpCurl(staticdata.websiteSection, { enableCache: true }),
      ecpCurl(staticdata.advertisingHeader, { enableCache: true }),
      ecpCurl(`${baseURL}${wwwApi.selectionProductDetail}`, {
        query: {
          uuid: id,
        },
      }),
      ecpCurl(`${baseURL}${WwwNodeApi.newWwwStockpileHistorySwitch}`),
    ]);

  if (notFound) {
    return {
      notFound: true,
    };
  }

  const { detailCodeList } = selectionProductDetailData;

  if (selectionProductDetailData && wwwStockpileHistorySwitch?.switch) {
    selectionProductDetailData.showStockBtn = wwwStockpileHistorySwitch?.switch === 'yes';
  }

  const { searchResult } = await ecpCurl(listApi.selectionPlanRelatedProducts, {
    query: {
      productCodes: detailCodeList,
    },
  });

  return {
    props: {
      id,
      resourceData,
      websiteSectionList,
      headerAd,
      selectionProductDetailData,
      searchResultData: searchResult,
    },
    revalidate: 60,
  };
}
